import { List } from "immutable";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import SplitView from "../../components/splitView";
import {
  LAYOUT_VIEW,
  MINDMAP_VIEW,
  MIXED_VIEWS,
  NARRATIVE_VIEW,
  ORIENTATION_COLUMN,
  ORIENTATION_ROW,
  OUTLINE_VIEW,
  RELATIONSHIP_VIEW,
  SPREADSHEET_VIEW,
  SUBWAY_VIEW,
  TIMELINE_VIEW
} from "../../constants/identifiers";
import PubSubTopics from "../../constants/pubSubTopics";
import Dimensions from "../../constants/styles/dimensions";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import { typeSelectors } from "../../modules/definition/types";
import { immutableRecords, PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { trackEvent } from "../../utils/trackAnalytics";
import MindmapViewLayout from "../mindmapView/MindmapViewLayout";
import NarrativeViewLayout from "../narrativeView/NarrativeViewLayout";
import OutlineViewLayout from "../outlineView/OutlineViewLayout";
import RelationshipViewLayout from "../relationshipView/RelationshipViewLayout";
import SpreadsheetViewLayout from "../spreadsheetView/SpreadsheetViewLayout";
import SubwayViewLayout from "../subwayView/SubwayViewLayout";
import TimelineViewLayout from "../timelineView/TimelineViewLayout";
import selectors from "./selectors";
import { WorkspaceContext } from "./WorkspaceContext";

// import debugUtils from "../../utils/debugUtils";

const MAX_VERTICAL_VIEWS = 3;
const MAX_HORIZONTAL_VIEWS = 5;

const isViewCollapsed = view => view && view.size && view.size.height <= Dimensions.COLLAPSE_WORKVIEW_HEIGHT;

const isLastChildCollapsed = viewLayout => {
  const { children } = viewLayout;
  return children && children.size > 1 && isViewCollapsed(children.last());
};

const isAllChildrenCollapsed = viewLayout => {
  const { children } = viewLayout;
  return children && children.size > 0 && children.every(c => isViewCollapsed(c));
};

const splitLastCollapsedChildFromSyncScroll = viewLayout => {
  const { children } = viewLayout;
  const lastChild = children.last();
  const lastHeight = lastChild.size.height;
  const newSyncScrollLayout =
    children.size > 2
      ? viewLayout.withMutations(rm => {
        rm.update("children", c => c.pop());
        rm.updateIn(["minSize", "height"], h => h - lastHeight);
        rm.updateIn(["size", "height"], h => h - lastHeight);
      })
      : children.get(0);
  const newViewLayout = viewLayout.withMutations(rm => {
    rm.set("childViewTypes", MIXED_VIEWS);
    rm.set("children", List([newSyncScrollLayout, lastChild]));
    rm.set("scrollSync", false);
  });

  return newViewLayout;
};

// If scrollSync view type (TimelineView, RelationshipView),
// should split the bottom collapsed one to avoid
// the scroll bar covering the view header.
// See https://aeontimeline.atlassian.net/browse/ATR-1025
// When all the views under the scrollSync are collapsed,
// no necessary to adjust.
// It may lead the wrong height calculation
// See https://aeontimeline.atlassian.net/browse/ATR-1828
const adjustViewLayoutIfLastChildCollapsed = viewLayout => {
  const viewType = viewLayout.get("childViewTypes");
  return (viewType === TIMELINE_VIEW || viewType === RELATIONSHIP_VIEW) &&
    isLastChildCollapsed(viewLayout) &&
    !isAllChildrenCollapsed(viewLayout)
    ? splitLastCollapsedChildFromSyncScroll(viewLayout)
    : viewLayout;
};

/**
 * Count the total number of views on the target orientation
 * @param {ViewLayoutDisplayRecord|ViewDisplayRecord} vl
 * @param {string} orientation Target orientation
 */
const countViewsOnOrientation = (vl, orientation) => {
  if (vl.viewType === LAYOUT_VIEW) {
    const { children } = vl;
    const rootCount = vl.orientation === orientation ? vl.children.count() : 1;
    const childrenCount = children.reduce((max, c) => {
      if (c.viewType === LAYOUT_VIEW) {
        max = Math.max(max, countViewsOnOrientation(c, orientation) - 1);
      }
      return max;
    }, 0);
    return rootCount + childrenCount;
  }
  return 1;
};

const hasTimelineView = openViews => openViews.children.find(v => v.viewType === TIMELINE_VIEW) !== undefined;

class Workspace extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.resizedViews = {};

    this.state = { shouldAutoScrollTimeline: false };

    this._workspaceContextState = {
      registerWorkViewCreateItemCallback: this.registerWorkViewCreateItemCallback.bind(this),
      // eslint-disable-next-line max-len
      unregisterWorkViewCreateItemCallback: this.unregisterWorkViewCreateItemCallback.bind(this),
      // eslint-disable-next-line max-len
      registerWorkViewCreateMarkerCallback: this.registerWorkViewCreateMarkerCallback.bind(this),
      // eslint-disable-next-line max-len
      unregisterWorkViewCreateMarkerCallback: this.unregisterWorkViewCreateMarkerCallback.bind(this)
    };
    this._createItemHandlersForView = {};
    this._createMarkerHandlersForView = {};
    this.onCreateItemReceived = this.onCreateItemReceived.bind(this);
    this.onCreateMarkerReceived = this.onCreateMarkerReceived.bind(this);
    this.onAppViewClick = this.onAppViewClick.bind(this);

    this.viewResized = this.viewResized.bind(this);
    this.dispatchSizes = this.dispatchSizes.bind(this);
    this.debouncedDispatchSizes = debounce(this.dispatchSizes, 250);
  }

  componentDidMount() {
    this._pubSubCreateItemToken = PubSub.subscribe(PubSubTopics.createItem, this.onCreateItemReceived);
    this._pubSubCreateMarkerToken = PubSub.subscribe(PubSubTopics.createMarker, this.onCreateMarkerReceived);
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges("Workspace", this.props, nextProps, true);
  // }

  componentDidUpdate(prevProps) {
    if (preprocessorQuery.isIos()) {
      if (!hasTimelineView(prevProps.openViews) && hasTimelineView(this.props.openViews)) {
        this.updateShouldAutoScrollTimeline(true);
      } else {
        this.updateShouldAutoScrollTimeline(false);
      }
    }
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubCreateItemToken);
    PubSub.unsubscribe(this._pubSubCreateMarkerToken);
  }

  updateShouldAutoScrollTimeline(newValue) {
    if (newValue !== this.state.shouldAutoScrollTimeline) {
      this.setState({ shouldAutoScrollTimeline: newValue });
    }
  }

  registerWorkViewCreateItemCallback = (viewId, callback) => {
    this._createItemHandlersForView[viewId] = callback;
  };

  unregisterWorkViewCreateItemCallback = viewId => {
    delete this._createItemHandlersForView[viewId];
  };

  registerWorkViewCreateMarkerCallback = (viewId, callback) => {
    this._createMarkerHandlersForView[viewId] = callback;
  };

  unregisterWorkViewCreateMarkerCallback = viewId => {
    delete this._createMarkerHandlersForView[viewId];
  };

  onAppViewClick(viewId) {
    if (viewId !== this.props.activeView) {
      this.props.onAppViewClick(viewId);
    }
  }

  onCreateItemReceived(msg, data) {
    // First see if there is a registered callback for additional messages
    const viewId = this.props.activeView;
    const handler = Object.prototype.hasOwnProperty.call(this._createItemHandlersForView, viewId)
      ? this._createItemHandlersForView[viewId]
      : null;

    // See if the active view wants to handle the event.
    // It will return true if it succeeds, and false if it fails
    let handled = handler ? handler(data.typeId, shouldOpenList) : false;
    // If handled, we are done.
    if (handled) {
      return;
    }

    // If not handled by active view, see if it belongs in a sidebar list, as if it is, we will just create the item
    // without referring to the selected workview (so no adding to timeline)
    const shouldOpenList = Boolean(this.props.sidebarTypes.find(t => t.id === data.typeId));
    if (shouldOpenList) {
      this.props.onCreateItem(data.typeId, shouldOpenList);
      return;
    }

    // Otherwise, we should create it ourselves
    this.props.onCreateItem(data.typeId, shouldOpenList);
  }

  onCreateMarkerReceived(_msg, _data) {
    // First see if there is a registered callback for additional messages
    const viewId = this.props.activeView;
    const handler = Object.prototype.hasOwnProperty.call(this._createMarkerHandlersForView, viewId)
      ? this._createMarkerHandlersForView[viewId]
      : null;
    // If the active view wants to handle the event, let it handle it
    // It will return true if it succeeds, and false if it fails
    let handled = handler ? handler() : false;
    // If handled, we are done.
    if (handled) {
      return;
    }
    // Otherwise, we should create it ourselves
    this.props.onCreateMarker();
  }

  viewResized(viewId, newSize) {
    this.resizedViews[viewId] = {
      width: newSize.width,
      height: newSize.height
    };
    this.debouncedDispatchSizes();
  }

  dispatchSizes() {
    // Apply to the current openViews byId
    const resizedViews = this.props.viewsById
      .map((v, id) => this.resizedViews[id])
      .filter(v => v)
      .toJS();
    this.props.onAppViewResize(resizedViews);
    this.resizedViews = {};
  }

  renderViewLayoutComponent = (
    Com,
    viewLayout,
    handlers,
    depth,
    canViewResize,
    viewIds,
    maxHorizontal,
    maxVertical
  ) => {
    return (
      <Com
        key={viewIds.join()}
        viewLayout={viewLayout}
        workViewHandlers={handlers}
        viewIds={viewIds}
        layoutDepth={depth}
        canViewResize={canViewResize}
        maxHorizontalViews={maxHorizontal}
        maxVerticalViews={maxVertical}
        shouldAutoScroll={this.state.shouldAutoScrollTimeline}
      />
    );
  };

  renderViewLayout = (viewType, viewLayout, handlers, depth, canViewResize, maxHorizontal, maxVertical) => {
    const viewIds = selectors.allViewIdsInLayout(viewLayout);

    const runRenderViewLayoutComponent = Com =>
      this.renderViewLayoutComponent(
        Com,
        viewLayout,
        handlers,
        depth,
        canViewResize,
        viewIds,
        maxHorizontal,
        maxVertical
      );

    if (viewType === TIMELINE_VIEW) {
      return runRenderViewLayoutComponent(TimelineViewLayout);
    } else if (viewType === NARRATIVE_VIEW) {
      return runRenderViewLayoutComponent(NarrativeViewLayout);
    } else if (viewType === MINDMAP_VIEW) {
      return runRenderViewLayoutComponent(MindmapViewLayout);
    } else if (viewType === RELATIONSHIP_VIEW) {
      return runRenderViewLayoutComponent(RelationshipViewLayout);
    } else if (viewType === SPREADSHEET_VIEW) {
      return runRenderViewLayoutComponent(SpreadsheetViewLayout);
    } else if (viewType === OUTLINE_VIEW) {
      return runRenderViewLayoutComponent(OutlineViewLayout);
    } else if (viewType === SUBWAY_VIEW) {
      return runRenderViewLayoutComponent(SubwayViewLayout);
    }
    return null;
  };

  renderChildView = (view, handlers, depth, canViewResize, layoutOrientation, maxHorizontal, maxVertical) => {
    // We only have a single child anyway.
    // But we will wrap it up in a ViewLayout to pass to the required XxxxViewLayout component
    // so that it has a consistent interface.
    const vl = immutableRecords.ViewLayoutDisplayRecord({
      orientation: layoutOrientation,
      viewType: LAYOUT_VIEW,
      childViewTypes: view.viewType,
      size: view.size,
      minSize: view.minSize,
      scrollSync: true,
      children: List([view])
    });
    return this.renderViewLayout(view.viewType, vl, handlers, depth, canViewResize, maxHorizontal, maxVertical);
  };

  renderSplitView = (
    origVl,
    handlers,
    scrollingAlreadyWrapped,
    scrollingIsLocked,
    parentKey,
    depth,
    canViewResize,
    maxHorizontal,
    maxVertical
  ) => {
    const vl = adjustViewLayoutIfLastChildCollapsed(origVl);

    const isHorizontal = vl.orientation === ORIENTATION_ROW;
    const children = vl.get("children");

    // Views count for each child, based on orientation
    const childrenViewsCounts = children.map(c =>
      countViewsOnOrientation(c, isHorizontal ? ORIENTATION_ROW : ORIENTATION_COLUMN)
    );

    const totalChildrenViewsCounts = childrenViewsCounts.reduce((sum, c) => sum + c, 0);

    const viewLayout = this.renderViewLayout(
      vl.childViewTypes,
      vl,
      handlers,
      depth,
      canViewResize,
      isHorizontal ? maxHorizontal : maxHorizontal,
      !isHorizontal ? maxVertical : maxVertical
    );
    // Single view type
    if (viewLayout) {
      return viewLayout;
    }

    // Mix view types

    // Max views for each child
    const childrenMaxHorizontalCounts = childrenViewsCounts.map(cvc =>
      isHorizontal ? maxHorizontal - totalChildrenViewsCounts + cvc : maxHorizontal
    );
    const childrenMaxVerticalCounts = childrenViewsCounts.map(cvc =>
      !isHorizontal ? maxVertical - totalChildrenViewsCounts + cvc : maxVertical
    );

    const orientation = vl.get("orientation");
    const wrapScrolling = vl.get("scrollSync");
    // const childTypes = v.childViewTypes;
    const viewCount = children.count();
    const relevantDimension = orientation === ORIENTATION_ROW ? "height" : "width";
    let splitProps = {
      vertical: orientation === ORIENTATION_COLUMN,
      constraints: children.map((c, index) => {
        const constraintRecord = immutableRecords.SplitConstraintsRecord({
          minSize: c.minSize[relevantDimension],
          canResize: canViewResize && index !== viewCount - 1,
          hasDivider: index !== viewCount - 1,
          dividerVisible: index !== viewCount - 1,
          defaultSize: Math.max(c.size[relevantDimension], 1)
        });
        // Setup the collapsable when orientation is row
        // which means the relevant dimension is the height
        return orientation === ORIENTATION_ROW
          ? constraintRecord.merge({
            preferredSize: c.size.height,
            canCollapse: true,
            // When the child is a scroll sync view, should set the collapse size being same as the min size
            // While the min size is calculated in calcSizeValueForLayout in appState selectors
            collapseSize: c.scrollSync ? c.minSize.height : Dimensions.COLLAPSE_WORKVIEW_HEIGHT
          })
          : constraintRecord;
      })
    };

    const childComponents = [];

    childComponents.push(
      ...this.renderChildren(
        children,
        handlers,
        scrollingAlreadyWrapped || wrapScrolling,
        scrollingIsLocked,
        parentKey,
        depth + 1,
        canViewResize,
        orientation,
        childrenMaxHorizontalCounts,
        childrenMaxVerticalCounts
      )
    );

    return (
      <SplitView key="workspaceTopLevelSplit" {...splitProps}>
        {childComponents}
      </SplitView>
    );
  };

  renderChildren = function(
    children,
    handlers,
    scrollingAlreadyWrapped,
    scrollingIsLocked,
    parentKey,
    depth,
    canViewResize,
    layoutOrientation,
    childrenMaxHorizontalCounts,
    childrenMaxVerticalCounts
  ) {
    return children.map((v, index) => {
      const splitKey = String(parentKey) + String(index);
      const maxHCount = childrenMaxHorizontalCounts.get(index);
      const maxVCount = childrenMaxVerticalCounts.get(index);
      if (v.has("children")) {
        return this.renderSplitView(
          v,
          handlers,
          scrollingAlreadyWrapped,
          scrollingIsLocked,
          splitKey,
          depth,
          canViewResize,
          maxHCount,
          maxVCount
        );
      } else {
        return this.renderChildView(v, handlers, depth, canViewResize, layoutOrientation, maxHCount, maxVCount);
      }
    });
  };

  render() {
    const handlers = {
      onClick: this.onAppViewClick,
      onClose: this.props.onAppViewClose,
      onMinimize: this.props.onAppViewMinimize,
      onPopout: this.props.onAppViewPopout,
      onSplitHorizontal: this.props.onAppViewSplitHorizontal,
      onSplitVertical: this.props.onAppViewSplitVertical,
      onFilter: this.props.onAppViewFilter,
      onResize: this.viewResized
    };

    const splitView = this.renderSplitView(
      this.props.openViews,
      handlers,
      false,
      this.props.scrollingIsLocked,
      "workspace",
      0,
      this.props.canViewResize,
      MAX_HORIZONTAL_VIEWS,
      MAX_VERTICAL_VIEWS
    );

    return <WorkspaceContext.Provider value={this._workspaceContextState}>{splitView}</WorkspaceContext.Provider>;
  }
}

Workspace.defaultProps = {
  canViewResize: true
};

Workspace.propTypes = {
  sidebarTypes: PropTypeShapes.itemTypeList.isRequired,
  openViews: PropTypeShapes.viewLayout.isRequired,
  viewsById: ImmutablePropTypes.map.isRequired,
  activeView: PropTypes.string.isRequired,
  scrollingIsLocked: PropTypes.bool.isRequired,
  onAppViewClick: PropTypes.func.isRequired,
  onAppViewClose: PropTypes.func.isRequired,
  onAppViewMinimize: PropTypes.func.isRequired,
  onAppViewPopout: PropTypes.func.isRequired,
  onAppViewSplitHorizontal: PropTypes.func.isRequired,
  onAppViewSplitVertical: PropTypes.func.isRequired,
  onAppViewResize: PropTypes.func.isRequired,
  onAppViewFilter: PropTypes.func.isRequired,
  onCreateItem: PropTypes.func.isRequired,
  onCreateMarker: PropTypes.func.isRequired,
  canViewResize: PropTypes.bool.isRequired
};

const mapStateToProps = state => {
  return {
    openViews: appStateSelectors.getCurrentViews(state.present),
    viewsById: appStateSelectors.getOpenViewsById(state.present),
    activeView: appStateSelectors.getActiveViewId(state.present),
    scrollingIsLocked: appStateSelectors.getScrollingIsLocked(state.present),
    sidebarTypes: typeSelectors.getSidebarTypes(state.present)
  };
};

const readonlyActions = {
  onAppViewClick: appStateOperations.setActiveView,
  onAppViewClose: (...args) => {
    trackEvent("removeView", "splitView");
    return appStateOperations.removeView(...args);
  },
  onAppViewMinimize: appStateOperations.minimizeView,
  onAppViewPopout: appStateOperations.popoutView,
  onAppViewSplitHorizontal: (...args) => {
    trackEvent("splitViewHorizontal", "splitView");
    return appStateOperations.splitViewHorizontal(...args);
  },
  onAppViewSplitVertical: (...args) => {
    trackEvent("splitViewVertical", "splitView");
    return appStateOperations.splitViewVertical(...args);
  },
  onAppViewResize: appStateOperations.resizeViews,
  onAppViewFilter: appStateOperations.toggleLocalFilterPanel
};

const editingActions = {
  onCreateItem: (typeId, openList) => {
    if (openList) {
      return batchOperations.openEntityListAndCreateItem(typeId);
    } else {
      return batchOperations.createAndSelectItem(typeId);
    }
  },
  onCreateMarker: batchOperations.createCalendarMarker
};

const mapDispatchToProps = dispatch => {
  return mergeDispatchToProps(readonlyActions, editingActions, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(Workspace);
